import type { OmniNavTarget } from '@/hooks/useOmniNavTargets'
import type { CSSProperties, KeyboardEvent as ReactKeyboardEvent } from 'react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useBennyAvailable } from '@/components/assistant/useBennyAvailable'
import { useOpenBenny } from '@/components/assistant/useOpenBenny'
import { useIsSidebarThemedChrome } from '@/components/layout/floating-top-bar'
import { useTheme } from '@/components/theme-provider'
import { useIsMobile } from '@/hooks/use-mobile'
import { useAccountType } from '@/hooks/useAccountType'
import { useEntityTerminology } from '@/hooks/useEntityTerminology'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import { useModuleSearch } from '@/hooks/useModuleSearch'
import { matchNavTargets, useOmniNavTargets } from '@/hooks/useOmniNavTargets'
import { usePermissions } from '@/hooks/usePermissions'
import { useResolvedDarkMode } from '@/hooks/useResolvedDarkMode'
import { useSubscriptionFeatures } from '@/hooks/useSubscriptionFeatures'
import { useSupplierTerminology } from '@/hooks/useSupplierTerminology'
import { ModuleSearchResult } from '@/lib/api/search'
import { getTicketSearchPath } from '@/lib/search-navigation'
import { cn } from '@/lib/utils'
import { cleanRecordIdentifier } from '@/lib/utils/record-identifier'
import { isTauri } from '@/lib/utils/tauri'
import { useOrganizationStore } from '@/store/organization.store'
import { useSearchPreferencesStore } from '@/store/search-preferences.store'
import {
  SIDEBAR_THEMES,
  useUIPreferencesStore,
} from '@/store/ui-preferences.store'
import { getOrgPath } from '@/utils/org-paths'
import {
  AddressBookIcon,
  ArrowSquareOutIcon,
  BuildingsIcon,
  CalendarIcon,
  ChatCircleDotsIcon,
  CheckSquareIcon,
  ClockCounterClockwiseIcon,
  CommandIcon,
  FileIcon,
  FileTextIcon,
  FolderOpenIcon,
  HandshakeIcon,
  MagnifyingGlassIcon,
  NoteIcon,
  ReceiptIcon,
  StackIcon,
  TicketIcon,
  TruckIcon,
  XIcon,
} from '@phosphor-icons/react'
import { useNavigate } from 'react-router-dom'

import type { FeatureFlagKeyType } from '@scrambl/shared'
import { ModuleTypeEnum, PermissionActionEnum } from '@scrambl/shared'

interface OmniSearchProps {
  className?: string
  compact?: boolean
  /**
   * Collapse to a round search button until the field is focused, its results
   * are open, or it holds a query. ⌘K still expands it.
   */
  collapsible?: boolean
  /** Reports expand/collapse so the surrounding bar can size around it. */
  onExpandedChange?: (expanded: boolean) => void
}

/**
 * OmniSearch - A Stack-like global search component for the title bar
 * Features:
 * - Centered search input that acts as a drag region when not focused
 * - Global keyboard shortcut (Cmd+K / Ctrl+K)
 * - Search results dropdown with navigation
 * - Supports entities, projects, and tasks
 */
export function OmniSearch({
  className,
  compact = false,
  collapsible = false,
  onExpandedChange,
}: OmniSearchProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [isFocused, setIsFocused] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()
  const { currentOrganization } = useOrganizationStore()
  // Group headings are capitalized like "Projects" and the vendor group.
  const { Plural: entityPlural } = useEntityTerminology()
  const { isPersonal } = useAccountType()
  const { Plural: supplierPlural } = useSupplierTerminology()
  const isMobile = useIsMobile()
  const { checkPermission } = usePermissions()
  const { checkFeature } = useSubscriptionFeatures()

  // Recent search history — shared with the /search page via localStorage
  const recentQueries = useSearchPreferencesStore(state => state.recentQueries)
  const addRecentQuery = useSearchPreferencesStore(
    state => state.addRecentQuery,
  )
  const removeRecentQuery = useSearchPreferencesStore(
    state => state.removeRecentQuery,
  )
  const clearRecentQueries = useSearchPreferencesStore(
    state => state.clearRecentQueries,
  )

  // Module type guard config — matches useNavigationItems.tsx
  const moduleGuards: Record<
    string,
    {
      featureFlag?: FeatureFlagKeyType
      subscriptionFeature?: string
      permission?: { module: ModuleTypeEnum; action: PermissionActionEnum }
    }
  > = {
    ENTITY: {
      featureFlag: 'ENTITIES' as FeatureFlagKeyType,
      subscriptionFeature: 'clients',
      permission: {
        module: ModuleTypeEnum.ENTITY,
        action: PermissionActionEnum.READ,
      },
    },
    PROJECT: {
      featureFlag: 'PROJECTS' as FeatureFlagKeyType,
      permission: {
        module: ModuleTypeEnum.PROJECT,
        action: PermissionActionEnum.READ,
      },
    },
    TASK: {
      featureFlag: 'TASKS' as FeatureFlagKeyType,
      permission: {
        module: ModuleTypeEnum.TASK,
        action: PermissionActionEnum.READ,
      },
    },
    TICKET: {
      featureFlag: 'TICKETS' as FeatureFlagKeyType,
      subscriptionFeature: 'tickets',
      permission: {
        module: ModuleTypeEnum.TICKET,
        action: PermissionActionEnum.READ,
      },
    },
    DEAL: {
      featureFlag: 'DEALS' as FeatureFlagKeyType,
      subscriptionFeature: 'deals',
      permission: {
        module: ModuleTypeEnum.DEAL,
        action: PermissionActionEnum.READ,
      },
    },
    CONTACT: {
      featureFlag: 'CONTACTS' as FeatureFlagKeyType,
      permission: {
        module: ModuleTypeEnum.CONTACT,
        action: PermissionActionEnum.READ,
      },
    },
    EVENT: {
      featureFlag: 'EVENTS' as FeatureFlagKeyType,
      subscriptionFeature: 'events',
      permission: {
        module: ModuleTypeEnum.EVENT,
        action: PermissionActionEnum.READ,
      },
    },
    CONTRACT: {
      featureFlag: 'CONTRACTS' as FeatureFlagKeyType,
      // The module's own plan feature, as in the sidebar: advanced_reports
      // hid contracts and invoices from search on plans that include them.
      subscriptionFeature: 'contracts',
      permission: {
        module: ModuleTypeEnum.CONTRACT,
        action: PermissionActionEnum.READ,
      },
    },
    INVOICE: {
      featureFlag: 'INVOICES' as FeatureFlagKeyType,
      subscriptionFeature: 'invoices',
      permission: {
        module: ModuleTypeEnum.INVOICE,
        action: PermissionActionEnum.READ,
      },
    },
    // Same gates as the sidebar's Vendors item: an empty guard listed
    // vendors where the module is off by flag or plan.
    SUPPLIER: {
      featureFlag: 'SUPPLIERS' as FeatureFlagKeyType,
      subscriptionFeature: 'suppliers',
      permission: {
        module: ModuleTypeEnum.SUPPLIER,
        action: PermissionActionEnum.READ,
      },
    },
    NOTE: {
      featureFlag: 'NOTES' as FeatureFlagKeyType,
      permission: {
        module: ModuleTypeEnum.NOTE,
        action: PermissionActionEnum.READ,
      },
    },
    DOCUMENT: {
      featureFlag: 'DOCUMENTS' as FeatureFlagKeyType,
      permission: {
        module: ModuleTypeEnum.DOCUMENT,
        action: PermissionActionEnum.READ,
      },
    },
    CUSTOM_COLLECTION: {
      featureFlag: 'COLLECTIONS' as FeatureFlagKeyType,
      subscriptionFeature: 'collections',
      permission: {
        module: ModuleTypeEnum.CUSTOM_COLLECTION,
        action: PermissionActionEnum.READ,
      },
    },
  }

  const featureKeys = useMemo(
    () =>
      Object.values(moduleGuards)
        .map(g => g.featureFlag)
        .filter(Boolean) as FeatureFlagKeyType[],
    [],
  )
  const { checkFeatureFlag } = useFeatureFlags(featureKeys)
  const visibleSearchTypes = useMemo(
    () =>
      Object.keys(moduleGuards).filter(type => {
        const guard = moduleGuards[type]
        if (!guard) return true
        if (
          guard.subscriptionFeature &&
          !checkFeature(guard.subscriptionFeature)
        )
          return false
        if (guard.featureFlag && !checkFeatureFlag(guard.featureFlag))
          return false
        if (
          guard.permission &&
          !checkPermission(guard.permission.module, guard.permission.action)
        )
          return false
        return true
      }),
    [checkFeature, checkFeatureFlag, checkPermission],
  )

  const isModuleVisible = (type: string) => {
    const guard = moduleGuards[type]
    if (!guard) return true
    if (guard.subscriptionFeature && !checkFeature(guard.subscriptionFeature))
      return false
    if (guard.featureFlag && !checkFeatureFlag(guard.featureFlag)) return false
    if (
      guard.permission &&
      !checkPermission(guard.permission.module, guard.permission.action)
    )
      return false
    return true
  }

  // Get theme info for styling
  const { theme } = useTheme()
  const sidebarTheme = useUIPreferencesStore(state => state.sidebarTheme)

  // Determine if we're in the Tauri desktop app (uses sidebar-themed title bar)
  const isDesktopApp = isTauri() && !isMobile

  // Stay in sync with resolved DOM theme class changes on initial load and refresh.
  const isDarkMode = useResolvedDarkMode(theme)

  // Get the current theme configuration
  const themeConfig =
    SIDEBAR_THEMES.find(t => t.id === sidebarTheme) || SIDEBAR_THEMES[0]

  // Determine text colors based on context:
  // - In Tauri desktop app: use sidebar theme colors
  // - In web header: use dark text on light bg (light mode) or light text on dark bg (dark mode)
  // The desktop title bar and the web header's floating islands are
  // sidebar-themed; the plain web header is white/black.
  const isSidebarThemedChrome = useIsSidebarThemedChrome()
  const useDarkText =
    isDesktopApp || isSidebarThemedChrome
      ? isDarkMode
        ? false
        : themeConfig.lightTextColor === 'dark'
      : !isDarkMode // Web header: dark text in light mode, light text in dark mode

  const { query, setQuery, groupedResults, isLoading, clearSearch } =
    useModuleSearch({
      limit: 20,
      minSearchLength: 1,
      debounceMs: 250, // Slightly longer debounce for smoother UX
      allowedTypes: visibleSearchTypes,
    })

  const bennyAvailable = useBennyAvailable()
  const openBenny = useOpenBenny()
  const showBenny = bennyAvailable && query.trim().length > 0
  const bennyFirst =
    showBenny &&
    /^(create|add|move|assign|close|summarize|find)\b/i.test(query.trim())
  const leadingBennyCount = bennyFirst ? 1 : 0

  // Navigation destinations (modules + settings pages) matched client-side
  // against the raw query so they appear instantly, ahead of record results.
  const navTargets = useOmniNavTargets(isOpen || isFocused || query.length > 0)
  const navMatches = matchNavTargets(navTargets, query)
  const moduleMatches = navMatches.filter(t => t.group === 'module').slice(0, 5)
  const settingsMatches = navMatches
    .filter(t => t.group === 'settings')
    .slice(0, 6)
  const flatNavMatches = [...moduleMatches, ...settingsMatches]

  // Flatten results for keyboard navigation, filtered by module visibility.
  const flatResults = (() => {
    const allResults = [
      ...groupedResults.ENTITY,
      ...groupedResults.PROJECT,
      ...groupedResults.TASK,
      ...groupedResults.TICKET,
      ...groupedResults.DEAL,
      ...groupedResults.CONTACT,
      ...groupedResults.EVENT,
      ...groupedResults.CONTRACT,
      ...groupedResults.INVOICE,
      ...groupedResults.SUPPLIER,
      ...groupedResults.NOTE,
      ...groupedResults.DOCUMENT,
      ...groupedResults.CUSTOM_COLLECTION,
    ]
    return allResults.filter(r => isModuleVisible(r.type))
  })()

  const hasResults = flatResults.length > 0
  const hasNavMatches = flatNavMatches.length > 0
  const hasAnyResults = hasResults || hasNavMatches || showBenny
  const bennyIndex = bennyFirst ? 0 : flatNavMatches.length + flatResults.length
  // With an empty input we show recent searches instead of results.
  // Keep the dropdown list short here — the full history lives on /search.
  const showRecents = isOpen && query.length === 0

  // Collapsible search stays open while it's in use or holds a query.
  const isExpanded = !collapsible || isFocused || isOpen || query.length > 0
  const isCollapsed = !isExpanded
  useEffect(() => {
    onExpandedChange?.(isExpanded)
  }, [isExpanded, onExpandedChange])
  const visibleRecents = showRecents ? recentQueries.slice(0, 5) : []
  // Nav matches come first in the selectable list; records are offset by them.
  const totalSelectable = showRecents
    ? visibleRecents.length
    : flatNavMatches.length + flatResults.length + (showBenny ? 1 : 0)
  // Show dropdown when: open AND (has results OR has query typed OR showing recents).
  // The empty-query case still renders the "Open search page" footer, so it always opens.
  // Don't hide dropdown while loading - just show loading indicator with existing results
  const showDropdown =
    isOpen && (hasAnyResults || query.length >= 1 || showRecents)

  // Get the icon for a module type
  const getModuleIcon = (type: string) => {
    switch (type) {
      case 'ENTITY':
        return <BuildingsIcon className="h-4 w-4" weight="duotone" />
      case 'PROJECT':
        return <FolderOpenIcon className="h-4 w-4" weight="duotone" />
      case 'TASK':
        return <CheckSquareIcon className="h-4 w-4" weight="duotone" />
      case 'TICKET':
        return <TicketIcon className="h-4 w-4" weight="duotone" />
      case 'DEAL':
        return <HandshakeIcon className="h-4 w-4" weight="duotone" />
      case 'CONTACT':
        return <AddressBookIcon className="h-4 w-4" weight="duotone" />
      case 'EVENT':
        return <CalendarIcon className="h-4 w-4" weight="duotone" />
      case 'CONTRACT':
        return <FileTextIcon className="h-4 w-4" weight="duotone" />
      case 'INVOICE':
        return <ReceiptIcon className="h-4 w-4" weight="duotone" />
      case 'SUPPLIER':
        return <TruckIcon className="h-4 w-4" weight="duotone" />
      case 'NOTE':
        return <NoteIcon className="h-4 w-4" weight="duotone" />
      case 'DOCUMENT':
        return <FileIcon className="h-4 w-4" weight="duotone" />
      case 'CUSTOM_COLLECTION':
        return <StackIcon className="h-4 w-4" weight="duotone" />
      default:
        return null
    }
  }

  // Get the path for a module
  const getModulePath = (result: ModuleSearchResult) => {
    if (!currentOrganization?.slug) return '/'

    switch (result.type) {
      case 'ENTITY':
        return getOrgPath(currentOrganization.slug, `/entities/${result.id}`)
      case 'PROJECT':
        return getOrgPath(currentOrganization.slug, `/projects/${result.id}`)
      case 'TASK':
        return getOrgPath(currentOrganization.slug, `/tasks/${result.id}`)
      case 'TICKET':
        return getTicketSearchPath(currentOrganization.slug, result)
      case 'DEAL':
        return getOrgPath(currentOrganization.slug, `/deals/${result.id}`)
      case 'CONTACT':
        return getOrgPath(currentOrganization.slug, `/contacts/${result.id}`)
      case 'EVENT':
        return getOrgPath(currentOrganization.slug, `/events/${result.id}`)
      case 'CONTRACT':
        return getOrgPath(currentOrganization.slug, `/contracts/${result.id}`)
      case 'INVOICE':
        return getOrgPath(currentOrganization.slug, `/invoices/${result.id}`)
      case 'SUPPLIER':
        return getOrgPath(currentOrganization.slug, `/suppliers/${result.id}`)
      case 'NOTE':
        return getOrgPath(currentOrganization.slug, `/notes/${result.id}`)
      case 'DOCUMENT':
        return getOrgPath(currentOrganization.slug, `/documents/${result.id}`)
      case 'CUSTOM_COLLECTION':
        // Navigate to the collection entry page using collection slug
        return getOrgPath(
          currentOrganization.slug,
          `/collections/${result.collectionSlug}/${result.id}`,
        )
      default:
        return '/'
    }
  }

  // Navigate to selected result
  const handleAskBenny = () => {
    openBenny(query.trim())
    setIsOpen(false)
    clearSearch()
    inputRef.current?.blur()
  }

  const handleSelect = (result: ModuleSearchResult) => {
    const path = getModulePath(result)
    navigate(path)
    setIsOpen(false)
    clearSearch()
    inputRef.current?.blur()
  }

  // Navigate to a module or settings destination
  const handleSelectNav = (target: OmniNavTarget) => {
    if (!currentOrganization?.slug) return
    navigate(getOrgPath(currentOrganization.slug, target.path))
    setIsOpen(false)
    clearSearch()
    inputRef.current?.blur()
  }

  // Navigate to the full search page, optionally carrying a query
  const goToSearchPage = (searchQuery?: string) => {
    const suffix = searchQuery ? `?q=${encodeURIComponent(searchQuery)}` : ''
    navigate(getOrgPath(currentOrganization?.slug || '', `/search${suffix}`))
    setIsOpen(false)
    clearSearch()
    inputRef.current?.blur()
  }

  // Handle keyboard navigation
  const handleKeyDown = (e: ReactKeyboardEvent) => {
    if (!showDropdown) return

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setSelectedIndex(prev => (prev < totalSelectable - 1 ? prev + 1 : 0))
        break
      case 'ArrowUp':
        e.preventDefault()
        setSelectedIndex(prev =>
          prev > 0 ? prev - 1 : Math.max(0, totalSelectable - 1),
        )
        break
      case 'Enter':
        e.preventDefault()
        if (showRecents) {
          const recentQuery = visibleRecents[selectedIndex]
          if (recentQuery) goToSearchPage(recentQuery)
        } else if (showBenny && selectedIndex === bennyIndex) {
          handleAskBenny()
        } else if (selectedIndex - leadingBennyCount < flatNavMatches.length) {
          const target = flatNavMatches[selectedIndex - leadingBennyCount]
          if (target) handleSelectNav(target)
        } else if (
          flatResults[selectedIndex - leadingBennyCount - flatNavMatches.length]
        ) {
          handleSelect(
            flatResults[
              selectedIndex - leadingBennyCount - flatNavMatches.length
            ],
          )
        }
        break
      case 'Escape':
        e.preventDefault()
        setIsOpen(false)
        clearSearch()
        inputRef.current?.blur()
        break
    }
  }

  // Reset selected index when results change (use total length to detect actual result changes)
  useEffect(() => {
    setSelectedIndex(0)
  }, [totalSelectable, query, bennyFirst])

  // Commit the query to recent history once the user stops typing.
  // Mirrors the debounce on the /search page so both surfaces record the same way.
  const queryCommitTimer = useRef<ReturnType<typeof setTimeout>>(undefined)
  useEffect(() => {
    clearTimeout(queryCommitTimer.current)
    const trimmed = query.trim()
    if (trimmed) {
      queryCommitTimer.current = setTimeout(() => addRecentQuery(trimmed), 1000)
    }
    return () => clearTimeout(queryCommitTimer.current)
  }, [query, addRecentQuery])

  // Global keyboard shortcut (Cmd+K / Ctrl+K)
  useEffect(() => {
    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        inputRef.current?.focus()
        setIsOpen(true)
      }
    }

    window.addEventListener('keydown', handleGlobalKeyDown)
    return () => window.removeEventListener('keydown', handleGlobalKeyDown)
  }, [])

  // Close dropdown when clicking outside (but keep the search state for when user refocuses)
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false)
        // Don't clear search - let user come back to their results
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Dynamic text classes for the INPUT (based on sidebar theme - for title bar)
  const inputTextClasses = useDarkText ? 'text-black' : 'text-white'
  const inputTextMutedClasses = useDarkText ? 'text-black/50' : 'text-white/50'
  const inputTextSubtleClasses = useDarkText ? 'text-black/40' : 'text-white/40'
  const inputHoverBgClasses = useDarkText
    ? 'hover:bg-black/10'
    : 'hover:bg-white/10'
  const inputActiveBgClasses = useDarkText ? 'bg-black/15' : 'bg-white/15'
  const inputBorderClasses = useDarkText ? 'border-black/20' : 'border-white/20'
  const inputBgSubtleClasses = useDarkText ? 'bg-black/5' : 'bg-white/5'
  const inputBgHoverClasses = useDarkText
    ? 'hover:bg-black/10 hover:border-black/10'
    : 'hover:bg-white/10 hover:border-white/10'

  // Dynamic text colors for the DROPDOWN (based on dark/light mode - solid background)
  // Using inline styles to ensure they override any parent CSS rules
  // Theme tokens already resolve per mode, so the same values serve both
  const dropdownTextColor = 'hsl(var(--foreground))'
  const dropdownTextMutedColor = 'hsl(var(--muted-foreground))'
  const dropdownTextSubtleColor = 'hsl(var(--muted-foreground) / 0.7)'
  const dropdownHoverBgClasses = isDarkMode
    ? 'hover:bg-white/10'
    : 'hover:bg-accent'
  const dropdownActiveBgClasses = isDarkMode ? 'bg-white/15' : 'bg-accent'

  // Footer row that opens the full search page — always present in the dropdown
  const renderFooterLink = (label: string, searchQuery?: string) => (
    <button
      onClick={() => goToSearchPage(searchQuery)}
      className="flex w-full items-center justify-center gap-1.5 border-t py-2.5 text-xs font-medium transition-colors hover:opacity-80"
      style={{
        color: dropdownTextMutedColor,
        borderColor: 'hsl(var(--border))',
      }}
    >
      {label}
      <ArrowSquareOutIcon className="h-3.5 w-3.5" />
    </button>
  )

  // Render recent searches — shown when the input is empty
  const renderRecents = () => {
    if (visibleRecents.length === 0) return null

    return (
      <div className="py-1">
        <div className="flex items-center justify-between px-3 py-1.5">
          <div
            className="flex items-center gap-1.5 text-xs font-medium"
            style={{ color: dropdownTextSubtleColor }}
          >
            <ClockCounterClockwiseIcon className="h-3.5 w-3.5" />
            Recent searches
          </div>
          <button
            onClick={clearRecentQueries}
            className="text-xs transition-colors hover:opacity-80"
            style={{ color: dropdownTextSubtleColor }}
          >
            Clear all
          </button>
        </div>
        {visibleRecents.map((recentQuery, i) => {
          const isSelected = i === selectedIndex

          return (
            <div
              key={recentQuery}
              className={cn(
                'group flex items-center transition-colors',
                dropdownHoverBgClasses,
                isSelected && dropdownActiveBgClasses,
              )}
              onMouseEnter={() => setSelectedIndex(i)}
            >
              <button
                onClick={() => goToSearchPage(recentQuery)}
                className="flex min-w-0 flex-1 items-center gap-3 px-3 py-2 text-left text-sm"
                style={{ color: dropdownTextColor }}
              >
                <MagnifyingGlassIcon
                  className="h-4 w-4 flex-shrink-0"
                  style={{ color: dropdownTextSubtleColor }}
                />
                <span className="min-w-0 flex-1 truncate">{recentQuery}</span>
              </button>
              <button
                onClick={() => removeRecentQuery(recentQuery)}
                className="px-3 py-2 opacity-0 transition-opacity hover:text-destructive focus-visible:opacity-100 group-hover:opacity-100"
                style={{ color: dropdownTextSubtleColor }}
                title="Remove"
              >
                <XIcon className="h-3.5 w-3.5" />
              </button>
            </div>
          )
        })}
      </div>
    )
  }

  // Render a group of navigation destinations (modules / settings pages)
  const renderNavGroup = (
    title: string,
    targets: OmniNavTarget[],
    indexOffset: number,
  ) => {
    if (targets.length === 0) return null

    return (
      <div className="py-1">
        <div
          className="px-3 py-1.5 text-xs font-medium"
          style={{ color: dropdownTextSubtleColor }}
        >
          {title}
        </div>
        {targets.map((target, i) => {
          const globalIndex = leadingBennyCount + indexOffset + i
          const isSelected = globalIndex === selectedIndex
          const Icon = target.icon

          return (
            <button
              key={target.id}
              className={cn(
                'flex w-full items-center gap-3 px-3 py-2 text-left text-sm transition-colors',
                dropdownHoverBgClasses,
                isSelected && dropdownActiveBgClasses,
              )}
              style={{ color: dropdownTextColor }}
              onClick={() => handleSelectNav(target)}
              onMouseEnter={() => setSelectedIndex(globalIndex)}
            >
              <span
                className="flex-shrink-0"
                style={{ color: dropdownTextMutedColor }}
              >
                <Icon className="h-4 w-4" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate font-medium">
                  {target.label}
                </span>
                {/* Reached a setting inside this page: name it. */}
                {target.matchedSetting && (
                  <span
                    className="block truncate text-xs"
                    style={{ color: dropdownTextMutedColor }}
                  >
                    {target.matchedSetting.title}
                  </span>
                )}
              </span>
              <ArrowSquareOutIcon
                className="h-3.5 w-3.5 flex-shrink-0 opacity-0 group-hover:opacity-100"
                style={{ color: dropdownTextSubtleColor }}
              />
            </button>
          )
        })}
      </div>
    )
  }

  // Render a group of results
  const renderGroup = (
    title: string,
    results: ModuleSearchResult[],
    type: string,
  ) => {
    if (results.length === 0) return null

    return (
      <div className="py-1">
        <div
          className="px-3 py-1.5 text-xs font-medium"
          style={{ color: dropdownTextSubtleColor }}
        >
          {title}
        </div>
        {results.map(result => {
          const globalIndex =
            leadingBennyCount +
            flatNavMatches.length +
            flatResults.indexOf(result)
          const isSelected = globalIndex === selectedIndex

          return (
            <button
              key={result.id}
              className={cn(
                'flex w-full items-center gap-3 px-3 py-2 text-left text-sm transition-colors',
                dropdownHoverBgClasses,
                isSelected && dropdownActiveBgClasses,
              )}
              style={{ color: dropdownTextColor }}
              onClick={() => handleSelect(result)}
              onMouseEnter={() => setSelectedIndex(globalIndex)}
            >
              <span
                className={cn(
                  'flex-shrink-0',
                  type === 'ENTITY' && 'text-blue-500',
                  type === 'PROJECT' && 'text-orange-500',
                  type === 'TASK' && 'text-emerald-500',
                  type === 'TICKET' && 'text-sky-500',
                  type === 'DEAL' && 'text-purple-500',
                  type === 'CONTACT' && 'text-cyan-500',
                  type === 'EVENT' && 'text-amber-500',
                  type === 'CONTRACT' && 'text-indigo-500',
                  type === 'INVOICE' && 'text-lime-500',
                  type === 'SUPPLIER' && 'text-rose-500',
                  type === 'NOTE' && 'text-yellow-500',
                  type === 'DOCUMENT' && 'text-slate-500',
                  type === 'CUSTOM_COLLECTION' && 'text-violet-500',
                )}
              >
                {getModuleIcon(type)}
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  {result.identifier && (
                    // A quiet inset chip: the key reads as metadata beside
                    // the name, never as a button. The color is inline
                    // because the title bar resets dropdown text colors.
                    <span
                      className="bg-surface-inset flex-shrink-0 rounded-md px-1.5 py-0.5 font-mono text-[11px] font-medium tabular-nums"
                      style={{ color: 'hsl(var(--muted-foreground))' }}
                    >
                      {cleanRecordIdentifier(result.identifier)}
                    </span>
                  )}
                  <span className="truncate font-medium">{result.name}</span>
                </div>
                {result.parentName && (
                  <div
                    className="truncate text-xs"
                    style={{ color: dropdownTextSubtleColor }}
                  >
                    {result.parentName}
                  </div>
                )}
              </div>
              <ArrowSquareOutIcon
                className="h-3.5 w-3.5 flex-shrink-0 opacity-0 group-hover:opacity-100"
                style={{ color: dropdownTextSubtleColor }}
              />
            </button>
          )
        })}
      </div>
    )
  }

  const renderBenny = () =>
    showBenny && (
      <button
        type="button"
        className={cn(
          'flex w-full items-center gap-3 px-3 py-2.5 text-left text-sm transition-colors',
          dropdownHoverBgClasses,
          selectedIndex === bennyIndex && dropdownActiveBgClasses,
        )}
        style={{ color: dropdownTextColor }}
        onClick={handleAskBenny}
        onMouseEnter={() => setSelectedIndex(bennyIndex)}
      >
        <ChatCircleDotsIcon className="h-4 w-4 shrink-0" weight="duotone" />
        <span className="min-w-0 flex-1">
          <span className="block truncate font-medium">
            Ask Benny: {query.trim()}
          </span>
          <span
            className="block text-xs"
            style={{ color: dropdownTextMutedColor }}
          >
            Open a draft with this page’s details
          </span>
        </span>
      </button>
    )

  return (
    <div
      ref={containerRef}
      className={cn(
        'relative',
        className,
        // Collapsible: anchored right, grows leftward when opened.
        collapsible && 'duration-base ease-ios ml-auto transition-[max-width]',
        collapsible && (isExpanded ? 'max-w-xl' : 'max-w-9'),
      )}
      // Allow parent drag region to work through this container — except
      // when collapsed, where the whole control is a button to click.
      data-tauri-drag-region={isDesktopApp && isExpanded ? true : undefined}
      style={
        isDesktopApp
          ? ({
              WebkitAppRegion: isExpanded ? 'drag' : 'no-drag',
            } as CSSProperties)
          : undefined
      }
    >
      {/* Search Input Container - Acts as drag region when not focused */}
      <div
        data-omni-input
        className={cn(
          'relative flex w-full items-center',
          collapsible && 'overflow-hidden',
          // Top-bar scale: a 32px field; collapsed, a 36px-wide button, the
          // title bar's icon-item width (its height comes from theme.css).
          !compact && 'h-8 text-sm',
          isCollapsed && 'w-9 cursor-pointer justify-center',
          'rounded-lg border transition-all duration-200',
          'focus-within:outline focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-primary',
          isDesktopApp && !isFocused && 'cursor-default',
          isFocused
            ? cn(
                inputActiveBgClasses,
                inputBorderClasses,
                useDarkText ? 'ring-1 ring-black/10' : 'ring-1 ring-white/10',
              )
            : cn(
                inputBgSubtleClasses,
                useDarkText ? 'border-black/30' : 'border-white/30',
                inputBgHoverClasses,
              ),
        )}
        // Only act as drag region when not focused (for Tauri); a collapsed
        // search is a click target, never a drag handle.
        data-tauri-drag-region={
          isDesktopApp && !isFocused && isExpanded ? true : undefined
        }
        style={
          isDesktopApp && !isFocused && isExpanded
            ? ({ WebkitAppRegion: 'drag' } as CSSProperties)
            : isDesktopApp && isCollapsed
              ? ({ WebkitAppRegion: 'no-drag' } as CSSProperties)
              : undefined
        }
      >
        <MagnifyingGlassIcon
          className={cn(
            'pointer-events-none absolute transition-colors',
            isCollapsed
              ? 'left-1/2 top-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2'
              : compact
                ? 'left-2 h-3.5 w-3.5'
                : 'left-3 h-4 w-4',
            isFocused ? inputTextClasses : inputTextMutedClasses,
          )}
          weight="regular"
        />
        <input
          ref={inputRef}
          type="text"
          spellCheck={false}
          value={query}
          onChange={e => {
            setQuery(e.target.value)
            setIsOpen(true)
          }}
          onFocus={() => {
            setIsFocused(true)
            setIsOpen(true)
          }}
          onBlur={() => setIsFocused(false)}
          aria-label="Search records and modules"
          onKeyDown={handleKeyDown}
          // A phone has room for one word beside the icon.
          placeholder={isMobile ? 'Search…' : 'Search records, modules…'}
          className={cn(
            'w-full bg-transparent focus:outline-none',
            // Collapsed: the input stays mounted (so ⌘K can focus it) but
            // invisible until it expands.
            isCollapsed && 'pointer-events-none opacity-0',
            // The right padding clears the ⌘K hint, which only shows from
            // sm up; below that it only needs to clear the clear button.
            compact
              ? 'h-7 pl-7 pr-8 text-xs sm:pr-14'
              : 'h-full pl-9 pr-9 text-sm sm:pr-16',
            inputTextClasses,
            useDarkText
              ? 'placeholder:text-black/40'
              : 'placeholder:text-white/40',
          )}
          style={{ WebkitAppRegion: 'no-drag' } as CSSProperties}
          // Collapsed, the button below is the tab stop.
          tabIndex={isCollapsed ? -1 : undefined}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          data-form-type="other"
          data-lpignore="true"
          data-1p-ignore="true"
        />

        {/* Collapsed: a real button opens the search on click, like the
            other title-bar buttons. Acting on mousedown instead let the
            desktop title bar's window-drag handling race the press, so the
            first click was often lost. */}
        {isCollapsed && (
          <button
            type="button"
            aria-label="Search"
            title="Search"
            onClick={() => inputRef.current?.focus()}
            className="absolute inset-0 cursor-pointer rounded-[inherit]"
            style={{ WebkitAppRegion: 'no-drag' } as CSSProperties}
          />
        )}

        {/* Keyboard shortcut hint */}
        {isExpanded && !isFocused && !query && (
          <div
            className={cn(
              'pointer-events-none absolute flex items-center gap-1',
              compact ? 'right-2 text-[10px]' : 'right-3 text-xs',
              inputTextSubtleClasses,
            )}
          >
            <kbd
              className={cn(
                'hidden h-5 items-center gap-0.5 rounded border px-1.5 font-mono text-[10px] font-medium sm:inline-flex',
                useDarkText
                  ? 'border-black/20 bg-black/5 text-black/50'
                  : 'border-white/20 bg-white/5 text-white/50',
              )}
            >
              {navigator.platform.includes('Mac') ? (
                <>
                  <CommandIcon
                    className={compact ? 'h-2 w-2' : 'h-2.5 w-2.5'}
                    weight="bold"
                  />
                  <span>K</span>
                </>
              ) : (
                <span>Ctrl+K</span>
              )}
            </kbd>
          </div>
        )}

        {/* Clear button */}
        {query && (
          <button
            aria-label="Clear search"
            onClick={() => {
              clearSearch()
              inputRef.current?.focus()
            }}
            className={cn(
              'absolute right-3 rounded p-0.5 transition-colors',
              inputHoverBgClasses,
              inputTextMutedClasses,
              useDarkText ? 'hover:text-black' : 'hover:text-white',
            )}
            style={{ WebkitAppRegion: 'no-drag' } as CSSProperties}
          >
            <XIcon className="h-3.5 w-3.5" weight="bold" />
          </button>
        )}
      </div>

      {/* Results Dropdown - Solid opaque background for readability */}
      {showDropdown && (
        <div
          data-omni-dropdown
          className={cn(
            'absolute left-0 right-0 top-full z-50 mt-2',
            'rounded-lg border shadow-2xl',
            'max-h-[400px] overflow-y-auto',
            'duration-200 animate-in fade-in-0 slide-in-from-top-2',
            // Use solid background colors - dark in dark mode, light card bg in light mode
            isDarkMode
              ? 'border-border bg-popover'
              : 'border-border bg-card shadow-lg',
          )}
        >
          {/* Loading indicator - shown as a subtle bar at the top when loading with existing results */}
          {isLoading && hasAnyResults && (
            <div className="h-0.5 animate-pulse bg-gradient-to-r from-transparent via-primary to-transparent" />
          )}

          {showRecents ? (
            <div className="py-1">
              {renderRecents()}
              {renderFooterLink('Open search page')}
            </div>
          ) : hasAnyResults ? (
            <div className="py-1">
              {bennyFirst && renderBenny()}
              {renderNavGroup('Go to', moduleMatches, 0)}
              {renderNavGroup(
                'Settings',
                settingsMatches,
                moduleMatches.length,
              )}
              {/* With only "Ask Benny" (or nav matches) showing, the thin bar
                  above was easy to miss and the list read as "no results". */}
              {isLoading && !hasResults && (
                <div
                  role="status"
                  className="flex items-center gap-2 px-3 py-2 text-sm"
                  style={{ color: dropdownTextMutedColor }}
                >
                  <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent" />
                  Searching…
                </div>
              )}
              {isModuleVisible('ENTITY') &&
                renderGroup(entityPlural, groupedResults.ENTITY, 'ENTITY')}
              {isModuleVisible('PROJECT') &&
                renderGroup('Projects', groupedResults.PROJECT, 'PROJECT')}
              {isModuleVisible('TASK') &&
                renderGroup('Tasks', groupedResults.TASK, 'TASK')}
              {isModuleVisible('TICKET') &&
                renderGroup('Tickets', groupedResults.TICKET, 'TICKET')}
              {isModuleVisible('DEAL') &&
                renderGroup('Deals', groupedResults.DEAL, 'DEAL')}
              {isModuleVisible('CONTACT') &&
                renderGroup(
                  isPersonal ? 'Address Book' : 'Contacts',
                  groupedResults.CONTACT,
                  'CONTACT',
                )}
              {isModuleVisible('EVENT') &&
                renderGroup('Events', groupedResults.EVENT, 'EVENT')}
              {isModuleVisible('CONTRACT') &&
                renderGroup('Contracts', groupedResults.CONTRACT, 'CONTRACT')}
              {isModuleVisible('INVOICE') &&
                renderGroup('Invoices', groupedResults.INVOICE, 'INVOICE')}
              {isModuleVisible('SUPPLIER') &&
                renderGroup(
                  supplierPlural,
                  groupedResults.SUPPLIER,
                  'SUPPLIER',
                )}
              {isModuleVisible('NOTE') &&
                renderGroup('Notes', groupedResults.NOTE, 'NOTE')}
              {isModuleVisible('DOCUMENT') &&
                renderGroup('Documents', groupedResults.DOCUMENT, 'DOCUMENT')}
              {isModuleVisible('CUSTOM_COLLECTION') &&
                renderGroup(
                  'Collections',
                  groupedResults.CUSTOM_COLLECTION,
                  'CUSTOM_COLLECTION',
                )}
              {!bennyFirst && renderBenny()}
              {/* View all results link */}
              {renderFooterLink('View all results', query)}
            </div>
          ) : isLoading ? (
            <div
              className="flex items-center justify-center py-8 text-sm"
              style={{ color: dropdownTextMutedColor }}
            >
              <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
              Searching…
            </div>
          ) : query.length >= 1 ? (
            <div className="text-center">
              <div
                className="py-6 text-sm"
                style={{ color: dropdownTextMutedColor }}
              >
                No results found for &ldquo;{query}&rdquo;
              </div>
              {renderFooterLink('Search in full page', query)}
            </div>
          ) : null}
        </div>
      )}
    </div>
  )
}
